import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  OAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  linkWithCredential
} from 'firebase/auth'
import { auth, db } from '@/firebase/config'
import {
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore'
import { createLog } from './logService'
import { trackUserLogin } from '@/utils/loginTracker'
import { ROLES } from '@/config/roles'
import {
  isYouthEnvironment,
  crossYouthBoundary
} from '@/config/youthEnvironment'
import { encryptFields, decryptFields } from './encryptionService'
import { validateEducatorEmail } from '@/utils/emailDomainValidation'
import sessionTracker from '@/utils/sessionTracker'
import {
  clearAuthRelatedStorage,
  markAuthSessionStart
} from '@/utils/authSessionExpiry'

export async function checkUsernameAvailability(username) {
  const usernameQuery = query(
    collection(db, 'usernames'),
    where('username', '==', username.toLowerCase())
  )
  const querySnapshot = await getDocs(usernameQuery)
  return querySnapshot.empty
}

export async function storeUsernameMapping(username, email, uid) {
  await setDoc(doc(db, 'usernames', uid), {
    username: username.toLowerCase(),
    email: email,
    uid: uid,
    createdAt: serverTimestamp()
  })
}

export async function getEmailByUsername(username) {
  const usernameQuery = query(
    collection(db, 'usernames'),
    where('username', '==', username.toLowerCase())
  )
  const querySnapshot = await getDocs(usernameQuery)

  if (querySnapshot.empty) {
    throw new Error('Username not found')
  }

  const doc = querySnapshot.docs[0]
  return doc.data().email
}

export async function signUp(
  email,
  password,
  role,
  heardAboutUs = '',
  authInstance = auth,
  dbInstance = db
) {
  // Backend validation: Check email domain for educators
  if (role === ROLES.SCHOOL_ADMIN) {
    const emailValidation = validateEducatorEmail(email, role)
    if (!emailValidation.isValid) {
      const error = new Error(
        emailValidation.error || 'Invalid email domain for educator'
      )
      error.code = 'auth/invalid-email-domain'
      throw error
    }
  }

  const userCredential = await createUserWithEmailAndPassword(
    authInstance,
    email,
    password
  )

  // Only require email verification for PARENT and SCHOOL_ADMIN roles
  const requiresEmailVerification =
    role === ROLES.PARENT || role === ROLES.SCHOOL_ADMIN

  const userData = {
    email: email,
    role: role,
    createdAt: serverTimestamp(),
    hasSubscription: false,
    lastDateOfConsent:
      role === ROLES.PARENT || role === ROLES.SCHOOL_ADMIN
        ? serverTimestamp()
        : null,
    emailVerified: !requiresEmailVerification, // Set to true for roles that don't need verification
    heardAboutUs: heardAboutUs
  }

  const encryptedData = encryptFields(userData)
  await setDoc(doc(dbInstance, 'users', userCredential.user.uid), encryptedData)

  return userCredential
}

export async function schoolTeacherSignUp(
  email,
  password,
  firstName,
  lastName,
  schoolId,
  uid,
  country,
  region,
  phone
) {
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  )

  const userData = {
    email: email,
    role: 'TEACHER',
    createdAt: serverTimestamp(),
    firstName,
    lastName,
    schoolId,
    status: 'ACCEPTED',
    country,
    region,
    phone
  }

  const encryptedData = encryptFields(userData)
  await setDoc(doc(db, 'users', userCredential.user.uid), encryptedData)

  await createLog({
    userId: userCredential.user.uid,
    collectionName: 'users',
    data: userData,
    schoolId: schoolId,
    action: 'CREATE_TEACHER'
  })

  await deleteDoc(doc(db, 'users', uid))

  // Send notification to school admin that teacher has accepted invitation
  try {
    const { createTeacherInvitationAcceptedNotification } = await import(
      '@/services/notifications/teachers/teacherNotificationService'
    )
    const teacherName = `${firstName} ${lastName}`.trim()
    await createTeacherInvitationAcceptedNotification(schoolId, teacherName)
  } catch (error) {
    console.error(
      'Error sending teacher invitation accepted notification:',
      error
    )
    // Don't throw error - notification failure shouldn't block account creation
  }

  return userCredential
}

export async function schoolAdminSignUp(email, password, firstName, lastName) {
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  )

  const userData = {
    email: email,
    role: 'SCHOOL_ADMIN',
    createdAt: serverTimestamp(),
    firstName,
    lastName,
    isSchoolOnboarded: false,
    status: 'ACCEPTED'
  }

  const encryptedData = encryptFields(userData)
  await setDoc(doc(db, 'users', userCredential.user.uid), encryptedData)

  await createLog({
    userId: userCredential.user.uid,
    collectionName: 'users',
    data: userData,
    schoolId: 'system',
    action: 'CREATE_SCHOOL_ADMIN'
  })

  return userCredential
}

export async function schoolStudentSignUp(
  email,
  password,
  firstName,
  lastName,
  schoolId,
  studentId,
  classId,
  docId,
  country,
  region,
  username = null
) {
  const userCredential = await createUserWithEmailAndPassword(
    auth,
    email,
    password
  )

  const userData = {
    email: email,
    role: 'STUDENT',
    createdAt: serverTimestamp(),
    firstName,
    lastName,
    schoolId,
    studentId,
    classId,
    docId,
    status: 'ACCEPTED',
    country,
    region,
    username: username
  }

  const encryptedData = encryptFields(userData)
  await setDoc(doc(db, 'users', userCredential.user.uid), encryptedData)

  // Store username mapping if provided
  if (username) {
    await storeUsernameMapping(username, email, userCredential.user.uid)
  }

  try {
    await createLog({
      userId: userCredential.user.uid,
      collectionName: 'users',
      data: userData,
      schoolId: schoolId,
      action: 'CREATE_STUDENT'
    })
  } catch (logError) {
    console.error('Non-critical: createLog failed after student signup:', logError)
  }

  if (docId) {
    await deleteDoc(doc(db, 'users', docId))
  }

  return userCredential
}

export async function signIn(identifier, password, authInstance = auth) {
  let email = identifier

  // Check if identifier is a username (not an email)
  if (!identifier.includes('@')) {
    try {
      email = await getEmailByUsername(identifier)
    } catch (error) {
      throw new Error('Invalid username or password')
    }
  }

  const userCredential = await signInWithEmailAndPassword(
    authInstance,
    email,
    password
  )
  markAuthSessionStart()
  await trackUserLogin(userCredential.user.uid)
  return userCredential
}

export async function logOut() {
  await sessionTracker.endSession()
  clearAuthRelatedStorage()
  const leavingYouthEnvironment = isYouthEnvironment()
  const result = await signOut(auth)
  // Leaving a child/student page: full load so /login gets its trackers back.
  if (leavingYouthEnvironment) {
    crossYouthBoundary('/login', { replace: true })
  }
  return result
}

export async function signInWithGoogle(authInstance = auth, dbInstance = db) {
  const provider = new GoogleAuthProvider()

  // Add requested OAuth scopes
  provider.addScope('https://www.googleapis.com/auth/userinfo.profile')
  provider.addScope('https://www.googleapis.com/auth/userinfo.email')

  // Force re-consent to show all permissoins
  provider.setCustomParameters({
    prompt: 'consent'
  })

  const result = await signInWithPopup(authInstance, provider)

  const userDoc = await getDoc(doc(dbInstance, 'users', result.user.uid))
  const firstName = result.user.displayName?.split(' ')[0] || ''
  const lastName = result.user.displayName?.split(' ')[1] || ''

  if (!userDoc.exists()) {
    const userData = {
      email: result.user.email,
      createdAt: serverTimestamp(),
      emailVerified: true,
      emailVerifiedAt: serverTimestamp(),
      role: 'UNDEFINED',
      firstName: firstName,
      lastName: lastName
    }

    const encryptedData = encryptFields(userData)
    await setDoc(doc(dbInstance, 'users', result.user.uid), encryptedData)
  } else {
    const encryptedUserData = userDoc.data()
    const userData = decryptFields(encryptedUserData)
    const updates = {}

    if (!userData.firstName && firstName) {
      updates.firstName = firstName
    }
    if (!userData.lastName && lastName) {
      updates.lastName = lastName
    }
    if (userData.emailVerified !== true) {
      updates.emailVerified = true
      updates.emailVerifiedAt = serverTimestamp()
    }

    if (Object.keys(updates).length > 0) {
      const encryptedUpdates = encryptFields(updates)
      await updateDoc(
        doc(dbInstance, 'users', result.user.uid),
        encryptedUpdates
      )
    }
  }

  markAuthSessionStart()
  await trackUserLogin(result.user.uid)
  return result
}

export async function signInWithApple(authInstance = auth, dbInstance = db) {
  const provider = new OAuthProvider('apple.com')
  const result = await signInWithPopup(authInstance, provider)

  const userDoc = await getDoc(doc(dbInstance, 'users', result.user.uid))
  const firstName = result.user.displayName?.split(' ')[0] || ''
  const lastName = result.user.displayName?.split(' ')[1] || ''

  if (!userDoc.exists()) {
    const userData = {
      email: result.user.email,
      role: 'UNDEFINED',
      createdAt: serverTimestamp(),
      firstName: firstName,
      lastName: lastName,
      photoURL: result.user.photoURL,
      emailVerified: true,
      emailVerifiedAt: serverTimestamp()
    }

    const encryptedData = encryptFields(userData)
    await setDoc(doc(dbInstance, 'users', result.user.uid), encryptedData)
  } else {
    const encryptedUserData = userDoc.data()
    const userData = decryptFields(encryptedUserData)
    const updates = {}

    if (!userData.firstName && firstName) {
      updates.firstName = firstName
    }
    if (!userData.lastName && lastName) {
      updates.lastName = lastName
    }
    if (!userData.photoURL && result.user.photoURL) {
      updates.photoURL = result.user.photoURL
    }
    if (userData.emailVerified !== true) {
      updates.emailVerified = true
      updates.emailVerifiedAt = serverTimestamp()
    }

    if (Object.keys(updates).length > 0) {
      const encryptedUpdates = encryptFields(updates)
      await updateDoc(
        doc(dbInstance, 'users', result.user.uid),
        encryptedUpdates
      )
    }
  }

  markAuthSessionStart()
  await trackUserLogin(result.user.uid)
  return result
}

export async function signInWithFacebook(authInstance = auth, dbInstance = db) {
  const provider = new FacebookAuthProvider()

  const result = await signInWithPopup(authInstance, provider)

  const userDoc = await getDoc(doc(dbInstance, 'users', result.user.uid))
  const firstName = result.user.displayName?.split(' ')[0] || ''
  const lastName = result.user.displayName?.split(' ')[1] || ''

  if (!userDoc.exists()) {
    const userData = {
      email: result.user.email,
      createdAt: serverTimestamp(),
      emailVerified: true,
      emailVerifiedAt: serverTimestamp(),
      role: 'UNDEFINED',
      firstName: firstName,
      lastName: lastName
    }

    const encryptedData = encryptFields(userData)
    await setDoc(doc(dbInstance, 'users', result.user.uid), encryptedData)
  } else {
    const encryptedUserData = userDoc.data()
    const userData = decryptFields(encryptedUserData)
    const updates = {}

    if (!userData.firstName && firstName) {
      updates.firstName = firstName
    }
    if (!userData.lastName && lastName) {
      updates.lastName = lastName
    }
    if (userData.emailVerified !== true) {
      updates.emailVerified = true
      updates.emailVerifiedAt = serverTimestamp()
    }

    if (Object.keys(updates).length > 0) {
      const encryptedUpdates = encryptFields(updates)
      await updateDoc(
        doc(dbInstance, 'users', result.user.uid),
        encryptedUpdates
      )
    }
  }

  markAuthSessionStart()
  await trackUserLogin(result.user.uid)
  return result
}

/**
 * Reset password using custom email template
 * @param {string} email - User email
 * @returns {Promise<void>}
 */
export async function resetPassword(email) {
  try {
    const { firebaseConfig } = await import('@/firebase/config')
    const { sendPasswordResetEmail } = await import('./passwordResetService')

    const response = await fetch(
      `${firebaseConfig.functionsUrl}/generatePasswordResetLink`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      }
    )

    if (!response.ok) {
      let errorMessage = 'Failed to generate reset link'
      try {
        const errorData = await response.json()
        errorMessage = errorData.error || errorMessage
      } catch (parseError) {
        if (response.status === 404) {
          errorMessage = 'User not found'
        } else if (response.status === 400) {
          errorMessage = 'Invalid email address'
        } else {
          errorMessage = `Failed to generate reset link (Status: ${response.status})`
        }
      }

      if (
        errorMessage.includes('User not found') ||
        errorMessage.includes('user-not-found')
      ) {
        throw new Error('No account found with this email address')
      }
      throw new Error(errorMessage)
    }

    const { resetLink } = await response.json()

    if (!resetLink) {
      throw new Error('Failed to generate reset link')
    }

    await sendPasswordResetEmail(email, resetLink)
  } catch (error) {
    if (
      error.message.includes('User not found') ||
      error.message.includes('user-not-found') ||
      error.message.includes('No account found')
    ) {
      throw new Error('No account found with this email address')
    }
    throw error
  }
}

/**
 * Check if user signed in with OAuth provider (Google, Facebook, Apple)
 * @returns {Object} { isOAuthUser: boolean, provider: string|null }
 */
export function checkAuthProvider() {
  const user = auth.currentUser

  if (!user) {
    return { isOAuthUser: false, provider: null, hasPassword: false }
  }

  // Check provider data to determine sign-in method
  const providerData = user.providerData

  // Check if user has email/password provider
  const hasEmailProvider = providerData.some(
    (provider) => provider.providerId === 'password'
  )

  // Check OAuth providers
  const oauthProvider = providerData.find(
    (provider) =>
      provider.providerId === 'google.com' ||
      provider.providerId === 'facebook.com' ||
      provider.providerId === 'apple.com'
  )
  if (oauthProvider && !hasEmailProvider) {
    return {
      isOAuthUser: true,
      provider: oauthProvider.providerId,
      hasPassword: false
    }
  }

  // User has both OAuth and password, or just password
  if (oauthProvider && hasEmailProvider) {
    return {
      isOAuthUser: true,
      provider: oauthProvider.providerId,
      hasPassword: true
    }
  }

  return {
    isOAuthUser: false,
    provider: hasEmailProvider ? 'password' : null,
    hasPassword: hasEmailProvider
  }
}

export async function changePassword(currentPassword, newPassword) {
  const user = auth.currentUser

  if (!user) {
    throw new Error('No user is currently signed in')
  }

  // Check if user has password authentication
  const authInfo = checkAuthProvider()

  if (authInfo.isOAuthUser && !authInfo.hasPassword) {
    throw new Error(
      `You signed in with ${authInfo.provider.replace(
        '.com',
        ''
      )}. To change your password, please visit your ${authInfo.provider.replace(
        '.com',
        ''
      )} account settings.`
    )
  }

  // Create credential with current password
  const credential = EmailAuthProvider.credential(user.email, currentPassword)

  // Reauthenticate user
  await reauthenticateWithCredential(user, credential)

  // Update password
  await updatePassword(user, newPassword)

  return { success: true }
}

export async function linkEmailPassword(password) {
  const user = auth.currentUser

  if (!user) {
    throw new Error('No user is currently signed in')
  }

  const authInfo = checkAuthProvider()

  if (authInfo.hasPassword) {
    throw new Error('This account already has a password set')
  }

  // Link email/password provider
  const credential = EmailAuthProvider.credential(user.email, password)
  await linkWithCredential(user, credential)

  return { success: true }
}
